/**
 * gardenPlotLogic — pure computation for seed growth visual stages.
 *
 * No Phaser imports. Maps a SeedRecord's growth progress to one of
 * four visual stages (soil, sprout, sapling, bloom) and computes
 * display strings for hover tooltips.
 *
 * @packageDocumentation
 */

/** Visual stage of a planted seed. */
export type GrowthStage = 'soil' | 'sprout' | 'sapling' | 'bloom';

/** Computed display state for a single garden plot cell. */
export interface PlotCellState {
  seedRecordId: string;
  stage: GrowthStage;
  /** 0..1 growth progress */
  progress: number;
  /** Percentage integer 0..100 */
  progressPercent: number;
  /** True when growth >= 100% (readyAt <= now). */
  harvestReady: boolean;
  /** Display label for the hover tooltip. */
  tooltipText: string;
}

/**
 * Compute the growth progress (0..1) for a seed given current time.
 */
export function computeGrowthProgress(plantedAt: number, readyAt: number, now: number): number {
  if (now >= readyAt) return 1;
  if (now <= plantedAt) return 0;
  const elapsed = now - plantedAt;
  const total = readyAt - plantedAt;
  if (total <= 0) return 1;
  return Math.min(1, elapsed / total);
}

/**
 * Map a 0..1 progress value to a visual growth stage.
 *   0.00 .. 0.25 => soil
 *   0.25 .. 0.50 => sprout
 *   0.50 .. 0.75 => sapling
 *   0.75 .. 1.00 => bloom
 */
export function progressToStage(progress: number): GrowthStage {
  if (progress < 0.25) return 'soil';
  if (progress < 0.50) return 'sprout';
  if (progress < 0.75) return 'sapling';
  return 'bloom';
}

/**
 * Build the tooltip text for a seed's current state.
 */
export function buildTooltipText(progress: number, harvestReady: boolean): string {
  if (harvestReady) return 'Ready to harvest!';
  const pct = Math.floor(progress * 100);
  return `Growing... ${pct}%`;
}

/**
 * Compute full plot cell state from a seed record's timing fields.
 */
export function computePlotCell(
  seedRecordId: string,
  plantedAt: number,
  readyAt: number,
  now: number,
): PlotCellState {
  const progress = computeGrowthProgress(plantedAt, readyAt, now);
  const stage = progressToStage(progress);
  const harvestReady = now >= readyAt;
  const progressPercent = Math.floor(progress * 100);
  const tooltipText = buildTooltipText(progress, harvestReady);
  return { seedRecordId, stage, progress, progressPercent, harvestReady, tooltipText };
}

/** Grid layout constants for the garden area in HomeScene. */
export const GARDEN_LAYOUT = {
  /** Top-left X of the garden area (relative to REFERENCE_WIDTH). */
  originX: 520,
  /** Top-left Y of the garden area. */
  originY: 320,
  /** Width of each plot cell. */
  cellW: 36,
  /** Height of each plot cell. */
  cellH: 36,
  /** Horizontal gap between cells. */
  gapX: 8,
  /** Vertical gap between cells. */
  gapY: 8,
  /** Max columns in the garden grid. */
  cols: 4,
  /** Max rows in the garden grid. */
  rows: 3,
} as const;

/**
 * Compute the center position of a plot cell at grid index.
 */
export function cellCenter(index: number): { x: number; y: number } {
  const col = index % GARDEN_LAYOUT.cols;
  const row = Math.floor(index / GARDEN_LAYOUT.cols);
  const x = GARDEN_LAYOUT.originX + col * (GARDEN_LAYOUT.cellW + GARDEN_LAYOUT.gapX) + GARDEN_LAYOUT.cellW / 2;
  const y = GARDEN_LAYOUT.originY + row * (GARDEN_LAYOUT.cellH + GARDEN_LAYOUT.gapY) + GARDEN_LAYOUT.cellH / 2;
  return { x, y };
}
